'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { FileXls, Lock, User, ArrowRight, WechatLogo } from '@phosphor-icons/react';
import { authApi } from '../services/api';

// Magnetic Button Component
function MagneticButton({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Button {...props}>{children}</Button>
    </motion.div>
  );
}

export function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isWeChatLoading, setIsWeChatLoading] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  // 处理微信登录
  const handleWeChatLogin = async () => {
    setIsWeChatLoading(true);
    try {
      const response = await authApi.getWeChatAuthURL();
      if (response.code === 0 && response.data) {
        // 保存 state 到 sessionStorage 用于回调验证
        sessionStorage.setItem('wechat_state', response.data.state);
        // 跳转到微信授权页面
        window.location.href = response.data.auth_url;
      } else {
        setError('获取微信登录链接失败');
      }
    } catch (err) {
      setError('微信登录服务暂不可用');
    } finally {
      setIsWeChatLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // 调用真实登录API
      const response = await authApi.login(username, password);
      
      if (response.code !== 0) {
        throw new Error(response.message || '登录失败');
      }

      const { user, tokens } = response.data;
      
      // 使用返回的token登录
      login(
        { 
          id: user.id, 
          username: user.username,
          email: user.email,
          role: user.role,
        },
        tokens.access_token
      );

      // 登录成功后跳转到首页
      navigate('/home', { replace: true });
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : '用户名或密码错误';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 20,
      },
    },
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex">
      {/* Left Side - Branding (Asymmetric Layout) */}
      <motion.div 
        className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }} />
        </div>
        
        {/* Floating Elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-zinc-700/30 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-8">
              <FileXls className="w-5 h-5 text-emerald-400" weight="fill" />
              <span className="text-sm font-medium text-white/90">DataFlow Engine</span>
            </div>
            
            <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              智能数据处理
              <br />
              <span className="text-emerald-400">从未如此简单</span>
            </h1>
            
            <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
              用自然语言描述您的数据处理需求，AI 将自动构建高效的工作流。无需编写代码，即可处理复杂的 Excel 和 CSV 数据。
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="flex gap-12 mt-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <div>
              <div className="text-3xl font-bold text-white">10x</div>
              <div className="text-sm text-zinc-500 mt-1">效率提升</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">50+</div>
              <div className="text-sm text-zinc-500 mt-1">内置算子</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">0</div>
              <div className="text-sm text-zinc-500 mt-1">代码编写</div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          className="w-full max-w-md"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Mobile Logo */}
          <motion.div variants={itemVariants} className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-900 mb-4 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">DataFlow Engine</h1>
          </motion.div>

          <motion.div variants={itemVariants}>
            <Card variant="elevated" className="overflow-hidden">
              <CardContent className="p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight">
                    欢迎回来
                  </h2>
                  <p className="text-zinc-500 mt-2 text-sm">
                    请输入您的账号信息以继续
                  </p>
                </div>

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                  >
                    {error}
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <Input
                    label="用户名"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    leftIcon={<User className="w-5 h-5" />}
                    placeholder="请输入用户名"
                    required
                  />

                  <Input
                    label="密码"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    leftIcon={<Lock className="w-5 h-5" />}
                    placeholder="请输入密码"
                    required
                  />

                  <div className="pt-2">
                    <MagneticButton
                      type="submit"
                      variant="primary"
                      size="lg"
                      isLoading={isLoading}
                      className="w-full"
                      rightIcon={<ArrowRight className="w-5 h-5" />}
                    >
                      登录
                    </MagneticButton>
                  </div>
                </form>

                {/* 微信登录 */}
                <div className="mt-6">
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-200" />
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-white text-zinc-400">其他登录方式</span>
                    </div>
                  </div>
                  <motion.button
                    type="button"
                    onClick={handleWeChatLogin}
                    disabled={isWeChatLoading}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50 hover:border-zinc-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isWeChatLoading ? (
                      <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
                    ) : (
                      <WechatLogo className="w-5 h-5 text-green-600" weight="fill" />
                    )}
                    <span className="font-medium">微信登录</span>
                  </motion.button>
                </div>

                <div className="mt-6 pt-6 border-t border-zinc-100">
                  <p className="text-xs text-zinc-400 text-center">
                    默认账号：test1 / 密码：test123
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Footer */}
          <motion.p 
            variants={itemVariants}
            className="text-center text-xs text-zinc-400 mt-8"
          >
            2026 DataFlow Engine (诸暨途源科技有限公司). All rights reserved.
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
}
